/**
 * 大海龟旅店对白数据（INN_ARGUMENT → OMEN_END）。
 * 引号内的原文全部来自 innText.ts（逐字）；其余为允许的环境刻画（只刻画人与地，不解释任何谜团）。
 */
import type { DialogueData, DialogueNode } from '@/core/types/contracts';
import { CAST, HECKLERS } from './innCast';
import { INN_NARRATION as N, INN_QUOTES as Q } from './innText';

/** 把顺序节点串成一段对白（自动补 next）。 */
function chain(id: string, nodes: Omit<DialogueNode, 'next'>[]): DialogueData {
  const linked: DialogueNode[] = nodes.map((n, i) => (i < nodes.length - 1 ? { ...n, next: nodes[i + 1]!.id } : { ...n }));
  return { id, start: nodes[0]!.id, nodes: linked };
}
const say = (id: string, speaker: string, text: string, extra: Partial<DialogueNode> = {}): Omit<DialogueNode, 'next'> => ({ id, speaker, text, ...extra });
const tell = (id: string, text: string, extra: Partial<DialogueNode> = {}): Omit<DialogueNode, 'next'> => ({ id, text, ...extra });

export const INN_DIALOGUES: DialogueData[] = [
  // ---------------------------------------------------------------- 进店前：观察与闲谈（可选）
  chain('inn-obs-arrive', [
    tell('a1', N.roof),
    tell('a2', N.turtles),
    tell('a3', N.weatherFolk),
  ]),
  chain('inn-obs-turtles', [
    tell('t1', N.turtles),
    say('t2', CAST.player, '……两只小海龟，摆得倒是端正。'),
  ]),
  chain('inn-obs-crowd', [
    tell('c1', N.crowded),
    say('c2', CAST.zhao, '人多。挤一挤，找个位置坐下。'),
  ]),
  chain('inn-obs-stairs', [
    tell('s1', N.mezzanine),
    tell('s2', N.upstairs),
  ]),
  chain('inn-obs-bar', [
    tell('b1', '酒台后头一排陶罐，罐口都用布塞着。老板正拿一块看不出颜色的布擦着杯子。'),
  ]),
  chain('inn-obs-roof-inside', [
    tell('r1', '抬头看，龟壳的里面一根一根的梁，被雨敲得嗡嗡响。灯笼挂在柱子上，随风晃。'),
  ]),
  chain('inn-talk-girls', [
    say('g1', 'patron-girl-a', '急什么，这雨下不大的，一会儿就停。'),
    say('g2', 'patron-girl-b', '站门口吹吹风，比里头凉快。里头都是酒气。'),
  ]),
  chain('inn-talk-elders', [
    say('e1', 'patron-elder-a', '南陆的雨，来得快去得快。年轻人不躲，我们老骨头躲一躲。'),
    say('e2', 'patron-elder-b', '躲雨是躲雨，进去喝一杯也好——趁机嘛。'),
  ]),
  chain('inn-talk-boss', [
    say('k1', CAST.boss, '客人——欢迎欢迎！坐，坐！酒，我这里的，南陆第一……唔，怎么说，第一好！', { emote: { actor: CAST.boss, emote: 'happy' } }),
    say('k2', CAST.player, '……先找个位子。'),
    say('k3', CAST.boss, '位子，有有有！那边，坐！'),
  ]),
  chain('inn-talk-barfly', [
    say('f1', 'patron-barfly-a', '老板的话，十句听三句就够了。酒倒是真的。'),
  ]),
  chain('inn-talk-drinker', [
    say('d1', 'patron-drinker-a', '这酒苦是苦，喝惯了就离不开。'),
  ]),
  chain('inn-talk-sailor', [
    say('m1', 'patron-sailor-a', '船上淋了一个月，这点雨算什么？坐下坐下。'),
  ]),
  chain('inn-talk-table', [
    say('w1', CAST.wu, '坐啊，位子空着呢！', { emote: { actor: CAST.wu, emote: 'exclaim' } }),
  ]),

  // ---------------------------------------------------------------- 落座、点苦酒、环顾（主线）
  chain('inn-sit-order-a', [
    tell('o1', N.squeezeIn),
    say('o2', CAST.player, '老板，来一杯酒。', { face: { actor: CAST.player, facing: 'up' } }),
    say('o3', CAST.boss, '酒！有——苦的，本地的酒。唔，你们中原人，怕是喝不惯……', { emote: { actor: CAST.boss, emote: 'exclaim' } }),
    say('o4', CAST.player, '苦的也行。', { face: { actor: CAST.player, facing: 'left' } }),
  ]),
  chain('inn-sit-order-b', [
    tell('o5', N.bitterWine, { sfx: 'cup-set' }),
  ]),
  chain('inn-look-around', [
    tell('l1', N.mezzanine),
    tell('l2', N.upstairs),
  ]),

  // ---------------------------------------------------------------- 争论（全部引号内容逐字）
  chain('inn-arg-intro', [
    tell('i1', N.argueStart, { sfx: 'table-slam' }),
  ]),
  chain('inn-arg-wu-1', [
    say('w1', CAST.wu, Q.wu1, { emotion: 'scorn', emote: { actor: CAST.wu, emote: 'angry' } }),
    tell('w2', N.wuLook),
  ]),
  chain('inn-arg-luo-1', [
    say('l1', CAST.luo, Q.luo1, { emotion: 'flushed', emote: { actor: CAST.luo, emote: 'flushed' } }),
    tell('l2', N.luoLook),
  ]),
  chain('inn-arg-wu-2', [
    say('w1', CAST.wu, Q.wu2, { emotion: 'scorn', emote: { actor: CAST.wu, emote: 'question' } }),
    tell('w2', N.wuDisbelief),
  ]),
  chain('inn-arg-luo-2', [
    say('l1', CAST.luo, Q.luo2a, { emotion: 'flushed' }),
    say('l2', CAST.luo, Q.luo2b, { emotion: 'earnest' }),
  ]),
  chain('inn-arg-wu-3a', [
    say('w1', CAST.wu, Q.wu3a, { emotion: 'scorn' }),
    tell('w2', N.wuStands),
  ]),
  chain('inn-arg-wu-3b', [
    say('w1', CAST.wu, Q.wu3b, { emotion: 'shout', sfx: 'shout' }),
  ]),
  chain('inn-arg-crowd', [
    tell('c1', N.crowdLaugh, { sfx: 'crowd-laugh' }),
    say('c2', HECKLERS[0], Q.heckle1, { emote: { actor: HECKLERS[0], emote: 'laugh' }, face: { actor: HECKLERS[0], facing: 'right' } }),
    say('c3', HECKLERS[1], Q.heckle2, { emote: { actor: HECKLERS[1], emote: 'laugh' }, face: { actor: HECKLERS[1], facing: 'left' } }),
    say('c4', HECKLERS[2], Q.heckle3, { emote: { actor: HECKLERS[2], emote: 'laugh' }, face: { actor: HECKLERS[2], facing: 'left' } }),
    tell('c5', N.chaos),
  ]),
  chain('inn-arg-wu-4', [
    tell('w1', N.wuTurns),
    say('w2', CAST.wu, Q.wu4, { emotion: 'smug' }),
  ]),
  chain('inn-arg-luo-dim', [
    tell('d1', N.luoDim, { emote: { actor: CAST.luo, emote: 'dim' }, face: { actor: CAST.luo, facing: 'down' } }),
  ]),

  // ---------------------------------------------------------------- 不祥的预感（不揭示任何真相）
  chain('inn-omen-1', [
    tell('m1', N.omen1),
  ]),
  chain('inn-omen-2', [
    tell('m2', N.omen2),
  ]),
  chain('inn-omen-3', [
    tell('m3', N.omen3),
  ]),
];

export const INN_DIALOGUE_IDS = INN_DIALOGUES.map((d) => d.id);
